import { useState, useEffect, useCallback } from 'react';
import { groupService, DiveGroup, CreateGroupRequest, GroupJoinModeValue } from '../services/groupService';

export function useGroups(userId: string | null) {
  const [groups, setGroups] = useState<DiveGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const g = await groupService.getMyGroups();
      setGroups(g);
    } catch {
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const createGroup = useCallback(async (name: string, description?: string, joinMode?: GroupJoinModeValue) => {
    const request: CreateGroupRequest = { name, description, joinMode };
    const group = await groupService.createGroup(request);
    await refresh();
    return group;
  }, [refresh]);

  const joinByCode = useCallback(async (shareCode: string) => {
    const group = await groupService.joinByCode(shareCode);
    await refresh();
    return group;
  }, [refresh]);

  const leaveGroup = useCallback(async (groupId: string) => {
    if (!userId) return;
    await groupService.removeMember(groupId, userId);
    await refresh();
  }, [userId, refresh]);

  return { groups, loading, error, refresh, createGroup, joinByCode, leaveGroup };
}
